import { useForm as createUseForm } from "@ant-design-vue/use";
import { defineAsyncComponent } from "vue";
import { createFieldInput, createFieldInputNumber, createFieldRadioGroup } from "../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../i18n";
import store from "../../../store";
import { createLoading } from "../../../utils/loading";
import { email, equalsLength, requiredWhen, validateError } from "../../../utils/validate";
import { putField } from "../../../utils/form";
import lodash from "lodash";
import router from "../../../router";

export const CIcon = defineAsyncComponent(() => import("../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const callForgotPasswordOtp = ({ form }) => store.dispatch(`resource/auth/forgotPasswordOtp`, { data: form, config: { isNotification: false } });

export const callContact = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "CONTACT", value: searchQuery });

export const onClickForgotPassword = ({ data }) => {
    return data.useForm
    .validate()
    .then(() => {
        data.success = false;
        setIsLoading(true);
        return callForgotPasswordOtp({ form: data.useForm.modelRef })
            .finally(() => setIsLoading(false))
            .then(res => {
                data.success = true;
                data.useForm.modelRef.userKey = res
                router.push({
                    name: "active-forgot-password",
                    params: { props: JSON.stringify({ form: data.useForm.modelRef }) },
                    query: { _timestamp: new Date().getTime() },
                });
            })
            .catch(error => {
                const message = error.response?.data?.message;
                if (message) {
                    data.errorMessage = i18n.global.t(message);
                }
            });
    })
    .catch(error => validateError({ error }));
};

export const createForm = () => {
    return {
        email: null,
        mobile: null,
        refContact: null,
        userKey: null
    };
};

export const createRules = ({data}) => {
    const modelRef = data.useForm?.modelRef || data.form;
    return {
        email: [requiredWhen({ when: () => modelRef.refContact?.key === "email" }), email],
        mobile: [requiredWhen({ when: () => modelRef.refContact?.key === "mobile" }), equalsLength(10)],
    };
};

export const createField = ({ props, data, refs }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    return putField({
        data,
        fields: [
            createFieldRadioGroup({
                key: `refContact`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                label: () => i18n.global.t("views.forgotPassword.contact"),
                inputProp: () =>
                    lodash.merge({
                        value: modelRef.refContact,
                        "onUpdate:value": (value) => {
                            modelRef.refContact = value
                            modelRef.email = null
                            modelRef.mobile = null
                        },
                        list: callContact,
                        optLabel: "value",
                    }),
            }),
            createFieldInput({
                visible: () => "email" === modelRef.refContact?.key,
                key: "email",
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                label: () => i18n.global.t("model.user.email"),
                inputProp: () => ({
                    value: modelRef.email,
                    "onUpdate:value": (value) => (modelRef.email = value),
                    onKeyup: (e) => {
                        if (e.keyCode === 13) {
                            onClickForgotPassword({ props, data, refs });
                        }
                    },
                    onBlur: () => useForm.validate("email").catch(() => {}),
                }),
            }),
            createFieldInputNumber({
                visible: () => "mobile" === modelRef.refContact?.key,
                key: `mobile`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                label: () => i18n.global.t("model.user.mobile"),
                inputProp: () =>
                    lodash.merge({
                        value: modelRef.mobile,
                        "onUpdate:value": (value) => (modelRef.mobile = value),
                        comma: false,
                        maxDecimal: 10,
                        onKeyup: (e) => {
                            if (e.keyCode === 13) {
                                onClickForgotPassword({ props, data, refs });
                            }
                        },
                    }),
            }),
        ],
    });
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    Promise.all([callContact({})]).then((arr) => {
        data.callContacts = arr[0];
        data.useForm.modelRef.refContact = data.callContacts?.find((d) => d.key === "email");
    });
};